import { useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Plus, X, Target, Library, Pencil, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import type { CompetitionHabit } from '@/types/competitions';
import type { HabitPreset } from '@/types/habits';
import { CATEGORY_ICONS } from '@/lib/constants/habitBadges';
import { parseStepsTarget } from '@/lib/health/stepsTarget';
import {
  applyHabitEdit,
  hasSameHabit,
  newCompetitionHabitId,
  presetToCompetitionHabit,
} from '@/lib/competitions/competitionHabits';
import { HabitPresetSelector } from '@/components/customer/HabitPresetSelector';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const createHabitSchema = (t: (key: string) => string) => z.object({
  name: z.string().trim().min(1, t('dashboard.manager.competitions.validation.habitNameRequired') || 'Habit name is required'),
  category: z.enum(['Exercises', 'Nutrition', 'Sleep', 'Stress', 'Relations', 'Addictions']),
  description: z.string().optional(),
  stepsTarget: z.string().optional(),
});
type HabitFormValues = z.infer<ReturnType<typeof createHabitSchema>>;

const EMPTY_HABIT: HabitFormValues = { name: '', category: 'Exercises', description: '', stepsTarget: '' };

const getCategoryColor = (category: string) => {
  const colors = {
    Exercises: 'bg-blue-100 text-blue-800',
    Nutrition: 'bg-green-100 text-green-800',
    Sleep: 'bg-purple-100 text-purple-800',
    Stress: 'bg-orange-100 text-orange-800',
    Relations: 'bg-pink-100 text-pink-800',
    Addictions: 'bg-red-100 text-red-800',
  };
  return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
};

/** Name, category, description and steps target: shared by "Add" and "Edit". */
function HabitFields({ form, categoryLocked }: { form: UseFormReturn<HabitFormValues>; categoryLocked: boolean }) {
  const { t } = useLanguage();
  const categories = [
    { value: 'Exercises', label: `${CATEGORY_ICONS.Exercises} ${t('habits.categories.exercises') || 'Exercises'}` },
    { value: 'Nutrition', label: `${CATEGORY_ICONS.Nutrition} ${t('habits.categories.nutrition') || 'Nutrition'}` },
    { value: 'Sleep', label: `${CATEGORY_ICONS.Sleep} ${t('habits.categories.sleep') || 'Sleep'}` },
    { value: 'Stress', label: `${CATEGORY_ICONS.Stress} ${t('habits.categories.stress') || 'Stress'}` },
    { value: 'Relations', label: `${CATEGORY_ICONS.Relations} ${t('habits.categories.relations') || 'Relations'}` },
    { value: 'Addictions', label: `${CATEGORY_ICONS.Addictions} ${t('habits.categories.addictions') || 'Addictions'}` },
  ];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dashboard.manager.competitions.habitName') || 'Habit Name'}</FormLabel>
              <FormControl>
                <Input placeholder={t('dashboard.manager.competitions.habitNamePlaceholder') || 'Enter habit name'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dashboard.manager.competitions.category') || 'Category'}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={categoryLocked}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('dashboard.manager.competitions.selectCategory') || 'Select a category'} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categoryLocked && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Lock className="h-3 w-3" />
                  {t('dashboard.manager.competitions.categoryLockedHint') ||
                    'Members have already logged this habit, so its category stays as it was logged.'}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dashboard.manager.competitions.habitDescription') || 'Description (Optional)'}</FormLabel>
            <FormControl>
              <Textarea
                placeholder={t('dashboard.manager.competitions.habitDescriptionPlaceholder') || 'Enter habit description'}
                className="resize-none"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="stepsTarget"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dashboard.manager.competitions.habitStepsTarget') || 'Steps target (optional)'}</FormLabel>
            <FormControl>
              <Input inputMode="numeric" {...field} value={field.value ?? ''} />
            </FormControl>
            <p className="text-xs text-muted-foreground">
              {t('dashboard.manager.competitions.habitStepsTargetHint') ||
                'Members who walk this many steps get this habit logged automatically.'}
            </p>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

interface CompetitionHabitsEditorProps {
  habits: CompetitionHabit[];
  onChange: (habits: CompetitionHabit[]) => void;
  /** Whether a habit's category is locked because it already has logs (namat#25). */
  isCategoryLocked?: (habitId: string) => boolean;
}

/**
 * The habits of a competition being created or edited: typed by hand, picked
 * from the admin's habit library (`habitPresets`, namat#24), and editable
 * after they were added (namat#25).
 */
export function CompetitionHabitsEditor({ habits, onChange, isCategoryLocked = () => false }: CompetitionHabitsEditorProps) {
  const [showAddHabit, setShowAddHabit] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { t, language } = useLanguage();
  const habitSchema = createHabitSchema(t);

  const habitForm = useForm<HabitFormValues>({ resolver: zodResolver(habitSchema), defaultValues: EMPTY_HABIT });
  const editForm = useForm<HabitFormValues>({ resolver: zodResolver(habitSchema), defaultValues: EMPTY_HABIT });

  const addHabit = (habitData: HabitFormValues) => {
    const newHabit: CompetitionHabit = {
      id: newCompetitionHabitId(),
      name: habitData.name.trim(),
      category: habitData.category,
      description: habitData.description,
    };
    const target = parseStepsTarget(habitData.stepsTarget ?? '');
    if (target !== undefined) newHabit.stepsTarget = target;

    onChange([...habits, newHabit]);
    habitForm.reset(EMPTY_HABIT);
    setShowAddHabit(false);
    toast.success(t('dashboard.manager.competitions.messages.habitAdded') || 'Habit added to competition');
  };

  const addFromLibrary = (preset: HabitPreset) => {
    const habit = presetToCompetitionHabit(preset, language, newCompetitionHabitId());
    if (hasSameHabit(habits, habit)) {
      toast.info(t('dashboard.manager.competitions.messages.habitAlreadyAdded') || 'This habit is already in the competition');
      return;
    }
    onChange([...habits, habit]);
    toast.success(
      (t('dashboard.manager.competitions.messages.libraryHabitAdded') || 'Added "{name}"').replace('{name}', habit.name)
    );
  };

  const removeHabit = (habitId: string) => {
    if (editingId === habitId) setEditingId(null);
    onChange(habits.filter(h => h.id !== habitId));
    toast.success(t('dashboard.manager.competitions.messages.habitRemoved') || 'Habit removed from competition');
  };

  const startEdit = (habit: CompetitionHabit) => {
    editForm.reset({
      name: habit.name,
      category: habit.category,
      description: habit.description ?? '',
      stepsTarget: habit.stepsTarget ? String(habit.stepsTarget) : '',
    });
    setEditingId(habit.id);
  };

  const saveEdit = (values: HabitFormValues) => {
    if (!editingId) return;
    onChange(habits.map(h => (h.id === editingId
      ? applyHabitEdit(h, {
        name: values.name,
        category: values.category,
        description: values.description ?? '',
        stepsTarget: values.stepsTarget ?? '',
      }, { categoryLocked: isCategoryLocked(h.id) })
      : h)));
    setEditingId(null);
    toast.success(t('dashboard.manager.competitions.messages.habitUpdated') || 'Habit updated');
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              {t('dashboard.manager.competitions.habits') || 'Competition Habits'}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={showLibrary ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setShowLibrary(v => !v)}
              >
                <Library className="h-4 w-4 mr-2" />
                {t('dashboard.manager.competitions.fromLibrary') || 'From habit library'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddHabit(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('dashboard.manager.competitions.addHabit') || 'Add Habit'}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {habits.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              {t('dashboard.manager.competitions.noHabitsYet') || 'No habits added yet. Click "Add Habit" to get started.'}
            </p>
          ) : (
            <div className="space-y-2">
              {habits.map((habit) => (editingId === habit.id ? (
                <div key={habit.id} className="p-3 border rounded-lg border-primary space-y-4">
                  <Form {...editForm}>
                    <HabitFields form={editForm} categoryLocked={isCategoryLocked(habit.id)} />
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={editForm.handleSubmit(saveEdit)}>
                        {t('dashboard.manager.competitions.saveHabit') || 'Save habit'}
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        {t('dashboard.manager.competitions.cancel') || 'Cancel'}
                      </Button>
                    </div>
                  </Form>
                </div>
              ) : (
                <div
                  key={habit.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Badge className={getCategoryColor(habit.category)}>
                      {CATEGORY_ICONS[habit.category as keyof typeof CATEGORY_ICONS]} {habit.category}
                    </Badge>
                    <div>
                      <h4 className="font-medium">{habit.name}</h4>
                      {habit.description && (
                        <p className="text-sm text-muted-foreground">
                          {habit.description}
                        </p>
                      )}
                      {habit.stepsTarget && (
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard.manager.competitions.habitStepsTarget') || 'Steps target'}: {habit.stepsTarget}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(habit)}
                      aria-label={t('dashboard.manager.competitions.editHabit') || 'Edit habit'}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeHabit(habit.id)}
                      aria-label={t('dashboard.manager.competitions.removeHabit') || 'Remove habit'}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Habit library (habitPresets) */}
      {showLibrary && (
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.manager.competitions.fromLibrary') || 'From habit library'}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('dashboard.manager.competitions.fromLibraryHint') || 'Tap a habit to add it. You can still type your own habit with "Add Habit".'}
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex flex-col">
              <HabitPresetSelector onSelectPreset={addFromLibrary} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Habit Form */}
      {showAddHabit && (
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.manager.competitions.addNewHabit') || 'Add New Habit'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...habitForm}>
              <div className="space-y-4">
                <HabitFields form={habitForm} categoryLocked={false} />

                <div className="flex gap-2">
                  <Button type="button" onClick={habitForm.handleSubmit(addHabit)}>
                    {t('dashboard.manager.competitions.addHabit') || 'Add Habit'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddHabit(false);
                      habitForm.reset(EMPTY_HABIT);
                    }}
                  >
                    {t('dashboard.manager.competitions.cancel') || 'Cancel'}
                  </Button>
                </div>
              </div>
            </Form>
          </CardContent>
        </Card>
      )}
    </>
  );
}
